import { useCallback, useMemo, useState } from "react";
import {
  useOrgOnboardingStatus,
  useOrgPlan,
  useUpdateOrgOnboardingStatus,
  type IntegrationName,
} from "@tellescope/tanstack-query";
import {
  deriveSetupFlow,
  getStatusAfterBack,
  getStatusAfterStep,
  getStatusForSelection,
  type FlowStep,
  type SetupNotification,
} from "../../setup-flow";

export interface UseSetupDialogFlowArgs {
  /** Surface a success notification to the host (e.g. a toast); forwarded to each step. */
  onSuccess?: (notification: SetupNotification) => void;
  /** Surface an error/warning notification to the host; forwarded to each step. */
  onError?: (notification: SetupNotification) => void;
}

export interface SetupDialogFlowState {
  plan: ReturnType<typeof useOrgPlan>["plan"];
  isLoading: boolean;
  /** Show the integration-selection step (no integrations selected, or returned to it via back). */
  showSelection: boolean;
  currentStep: FlowStep | undefined;
  selectedIntegrationNames: IntegrationName[];
  completedIntegrationNames: IntegrationName[];
  onComplete: () => Promise<void>;
  onBack: () => Promise<void>;
  onSelectionContinue: (selected: IntegrationName[]) => Promise<void>;
  /** Mark the current step skipped and advance to the next step. */
  onSkipStep: () => Promise<void>;
  onSuccess?: (notification: SetupNotification) => void;
  onError?: (notification: SetupNotification) => void;
}

/**
 * Drives the generic setup dialog: reads the plan and onboarding status, derives
 * the current step via {@link deriveSetupFlow}, and exposes step-advance /
 * back / selection handlers built on the status-transition helpers. Local
 * `showSelection` state lets back-navigation from the first step return to the
 * integration-selection screen without mutating the record.
 */
export const useSetupDialogFlow = ({ onSuccess, onError }: UseSetupDialogFlowArgs): SetupDialogFlowState => {
  const { plan, isLoading: planLoading } = useOrgPlan();
  const { status, isLoading: statusLoading } = useOrgOnboardingStatus();
  const { updateStatus, replaceStatus } = useUpdateOrgOnboardingStatus();
  const [showSelection, setShowSelection] = useState(false);

  const flow = useMemo(() => (plan ? deriveSetupFlow({ plan, status }) : undefined), [plan, status]);
  const currentStep = flow?.currentStep;

  // Source the active integrations from the flow, not the raw status record: a
  // plan whose integrations are all required has them in the flow before the
  // status record records any selection, so the orbit reflects them immediately.
  const selectedIntegrationNames = useMemo(() => flow?.selectedIntegrations ?? [], [flow]);

  const completedIntegrationNames = useMemo(
    () => selectedIntegrationNames.filter((integration) => status?.[integration] === true),
    [selectedIntegrationNames, status]
  );

  const onComplete = useCallback(async () => {
    if (!currentStep) return;
    await updateStatus(
      getStatusAfterStep({ integration: currentStep.integration, stepKey: currentStep.step.key, outcome: "complete" })
    );
  }, [currentStep, updateStatus]);

  const onSkipStep = useCallback(async () => {
    if (!currentStep) return;
    await updateStatus(
      getStatusAfterStep({ integration: currentStep.integration, stepKey: currentStep.step.key, outcome: "skipped" })
    );
  }, [currentStep, updateStatus]);

  const onBack = useCallback(async () => {
    if (!plan || !currentStep) return;
    const result = getStatusAfterBack({
      plan,
      status,
      integration: currentStep.integration,
      stepKey: currentStep.step.key,
    });
    if (result === "selection") {
      setShowSelection(true);
      return;
    }
    await replaceStatus(result);
  }, [currentStep, plan, status, replaceStatus]);

  const onSelectionContinue = useCallback(
    async (selected: IntegrationName[]) => {
      await replaceStatus(getStatusForSelection({ selected, status }));
      setShowSelection(false);
    },
    [replaceStatus, status]
  );

  return {
    plan,
    isLoading: planLoading || statusLoading,
    showSelection: !!flow?.needsIntegrationSelection || showSelection,
    currentStep,
    selectedIntegrationNames,
    completedIntegrationNames,
    onComplete,
    onBack,
    onSelectionContinue,
    onSkipStep,
    onSuccess,
    onError,
  };
};
